import React, { useState } from 'react';
import { Check, X, AlertTriangle, Clock, ChevronRight, Filter, Eye, Loader2, Shield } from 'lucide-react';
import { useEscalations, type Escalation } from '../hooks/useEscalations';
import HRLogo from './HRLogo';

// Types for pending approvals (mapped from Escalation)
interface PendingApproval {
    id: string;
    replicaName: string;
    teamName: string;
    action: string;
    riskLevel: 'high' | 'medium' | 'low';
    reason: string;
    proposedAt: string;
    details?: Record<string, unknown>;
    expiresAt?: string | null;
}

interface PendingApprovalsQueueProps {
    onViewDetails?: (approval: PendingApproval) => void;
    replicaId?: string; // Optional filter by replica
}

// Map Escalation to PendingApproval for display
function mapEscalationToApproval(e: Escalation): PendingApproval {
    let decisionDetails: { action?: string; reason?: string } = {};
    try {
        decisionDetails = JSON.parse(e.decisionContent) || {};
    } catch {
        // Use raw content if not JSON
    }

    return {
        id: e.id,
        replicaName: e.replicaName || 'Unknown Replica',
        teamName: 'Decision Guardian™', // Team context from contract would be ideal
        action: decisionDetails.action || e.decisionType || 'Pending Decision',
        riskLevel: e.riskLevel,
        reason: decisionDetails.reason || `${e.decisionType} requires human approval (FAIL-CLOSED)`,
        proposedAt: e.proposedAt,
        expiresAt: e.expiresAt,
    };
}

export const DecisionBadge: React.FC<{
    type: 'ai-proposed' | 'human-approved' | 'human-rejected' | 'blocking';
    timestamp?: string;
}> = ({ type, timestamp }) => {
    const config = {
        'ai-proposed': {
            label: 'AI Proposed',
            bgClass: 'bg-hig-accent/10',
            textClass: 'text-hig-accent',
            borderClass: 'border-hig-accent/20',
        },
        'human-approved': {
            label: 'Human Approved',
            bgClass: 'bg-hig-success/10',
            textClass: 'text-hig-success',
            borderClass: 'border-hig-success/20',
        },
        'human-rejected': {
            label: 'Human Rejected',
            bgClass: 'bg-hig-error/10',
            textClass: 'text-hig-error',
            borderClass: 'border-hig-error/20',
        },
        'blocking': {
            label: 'FAIL-CLOSED',
            bgClass: 'bg-orange-500/10',
            textClass: 'text-orange-500',
            borderClass: 'border-orange-500/20',
        },
    };

    const { label, bgClass, textClass, borderClass } = config[type];

    return (
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${bgClass} ${textClass} ${borderClass}`}>
            {type === 'ai-proposed' && <Shield size={12} />}
            {type === 'human-approved' && <Check size={12} />}
            {type === 'human-rejected' && <X size={12} />}
            {type === 'blocking' && <AlertTriangle size={12} />}
            <span>{label}</span>
            {timestamp && <span className="opacity-70">· {timestamp}</span>}
        </div>
    );
};

const PendingApprovalsQueue: React.FC<PendingApprovalsQueueProps> = ({ onViewDetails, replicaId }) => {
    // Use real API data
    const { escalations, loading, error, approve, reject } = useEscalations({
        status: 'PENDING',
        replicaId,
    });

    const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
    const [processing, setProcessing] = useState<string | null>(null);

    // Map to display format
    const approvals = escalations.map(mapEscalationToApproval);

    const filteredApprovals = filter === 'all'
        ? approvals
        : approvals.filter(a => a.riskLevel === filter);

    const handleApprove = async (id: string) => {
        setProcessing(id);
        await approve(id, 'Approved via dashboard');
        setProcessing(null);
    };

    const handleReject = async (id: string) => {
        setProcessing(id);
        await reject(id, 'Rejected via dashboard');
        setProcessing(null);
    };

    const getRiskBadge = (level: 'high' | 'medium' | 'low') => {
        const config = {
            high: 'bg-red-500/10 text-red-500 border-red-500/20',
            medium: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
            low: 'bg-green-500/10 text-green-500 border-green-500/20',
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase border ${config[level]}`}>
                {level}
            </span>
        );
    };

    return (
        <div className="space-y-4">
            {/* Header with filter */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <HRLogo
                        state="approval"
                        size={40}
                        aria-label="Pending human approval required"
                    />
                    <div>
                        <h3 className="font-semibold text-hig-text-primary">Pending Approvals</h3>
                        <p className="text-xs text-hr-state-approval font-medium">Human gate — review required</p>
                    </div>
                    {!loading && approvals.length > 0 && (
                        <span className="px-2 py-0.5 bg-hig-accent text-white text-xs font-bold rounded-full">
                            {approvals.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Filter size={14} className="text-hig-text-tertiary" />
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value as typeof filter)}
                        className="text-xs bg-hig-bg-tertiary border border-hig-border rounded-lg px-2 py-1 text-hig-text-secondary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                    >
                        <option value="all">All Priorities</option>
                        <option value="high">High Risk</option>
                        <option value="medium">Medium Risk</option>
                        <option value="low">Low Risk</option>
                    </select>
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="text-center py-8">
                    <Loader2 size={24} className="animate-spin text-hig-accent mx-auto mb-2" />
                    <p className="text-hig-text-secondary text-sm">Loading escalations...</p>
                </div>
            )}

            {/* Error state */}
            {error && !loading && (
                <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-hig-error/10 flex items-center justify-center mx-auto mb-3">
                        <AlertTriangle size={24} className="text-hig-error" />
                    </div>
                    <p className="text-hig-text-secondary text-sm">Failed to load escalations</p>
                    <p className="text-hig-text-tertiary text-xs mt-1">{error}</p>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && filteredApprovals.length === 0 && (
                <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-hig-success/10 flex items-center justify-center mx-auto mb-3">
                        <Check size={24} className="text-hig-success" />
                    </div>
                    <p className="text-hig-text-secondary text-sm">No pending approvals</p>
                    <p className="text-hig-text-tertiary text-xs mt-1">All decisions have been reviewed</p>
                </div>
            )}

            {/* Approval cards */}
            <div className="space-y-3">
                {filteredApprovals.map((approval) => (
                    <div
                        key={approval.id}
                        className={`card p-4 transition-all ${processing === approval.id ? 'opacity-50' : ''}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-hig-text-primary">{approval.replicaName}</span>
                                    <span className="text-hig-text-tertiary">·</span>
                                    <span className="text-sm text-hig-text-secondary">{approval.teamName}</span>
                                </div>
                                <p className="text-sm text-hig-text-primary mb-2">{approval.action}</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {getRiskBadge(approval.riskLevel)}
                                    <DecisionBadge type="ai-proposed" />
                                </div>
                            </div>
                        </div>

                        {/* Reason */}
                        <div className="bg-hig-bg-tertiary rounded-lg p-3 mb-3">
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={14} className="text-hig-warning mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-hig-text-secondary">{approval.reason}</p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-xs text-hig-text-tertiary">
                                <Clock size={12} />
                                <span>{approval.proposedAt}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {onViewDetails && (
                                    <button
                                        onClick={() => onViewDetails(approval)}
                                        className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-lg transition-colors"
                                        title="View Details"
                                    >
                                        <Eye size={16} />
                                    </button>
                                )}
                                <button
                                    onClick={() => handleReject(approval.id)}
                                    disabled={processing === approval.id}
                                    className="px-3 py-1.5 text-sm font-medium text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={() => handleApprove(approval.id)}
                                    disabled={processing === approval.id}
                                    className="px-3 py-1.5 text-sm font-medium bg-hig-success text-white hover:bg-hig-success/90 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Approve
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* View all link */}
            {approvals.length > 0 && (
                <button className="w-full text-center text-sm text-hig-accent hover:underline flex items-center justify-center gap-1 py-2">
                    View all pending decisions <ChevronRight size={14} />
                </button>
            )}
        </div>
    );
};

export default PendingApprovalsQueue;
